/**
 * ExecutionLog
 *
 * The execution-log tree shared between the bottom panel (`LogsPanel`) and
 * the expand-to-modal dialog (`MonitoringExpandDialog`). Renders triggers,
 * actions, agents with iterations, scopes (ForEach/Until/Switch/If/Scope)
 * with repetitions, tool actions, and chat messages.
 *
 * Extraction notes:
 * - Pure rendering; expand/selection state is owned by the host so that
 *   data-fetching effects (e.g. fetch-tool-actions-when-agent-expands)
 *   stay alongside the host's data props.
 * - Styles are intentionally co-located here so the dialog can pick up the
 *   same look without depending on LogsPanel internals.
 */

import React from 'react';
import {
  Text,
  Button,
  Spinner,
  mergeClasses,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import {
  ChevronDownRegular,
  ChevronRightRegular,
  PersonRegular,
  BotRegular,
  ArrowRepeatAllRegular,
} from '@fluentui/react-icons';
import type { AgentIterationDataError, MonitoringDataError, NodeExecution } from '@/types/monitoring';
import { DataDetailsErrors, DataDetailsWarning, ToolDetailsWarning } from './DataDetailsError';
import type {
  RepetitionInfo,
  AgentIterationInfo,
  ChatMessageEntry,
  AgentToolActionInfo,
} from '@/services/monitoringApi';
import {
  ExecutionListItem,
  type ExecutionItem,
  StatusIcon,
  isScopeNodeType,
  isIteratingScopeNodeType,
} from './ExecutionListItem';
import {
  isAgentExecution,
  hasAgentIterations as checkHasAgentIterations,
  formatExecutionDuration,
  getMonitoringDataErrors,
} from '@/utils/monitoring';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { formatTimeOfDay } from '@/utils/dateTime';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Build a lookup from chatHistory to map per-tool args and output/error.
 * Key format: "iteration-toolName-occurrenceIndex"
 *
 * Exported for testing.
 */
export function buildBuiltInToolLookup(chatHistory: ChatMessageEntry[]) {
  const resultsByKey = new Map<string, { content: unknown; status: string | undefined }>();
  const argsByKey = new Map<string, unknown>();

  // Index ToolResult entries by iteration + toolName + occurrence
  const resultOccurrences = new Map<string, number>();
  const filteredToolResults = chatHistory
    .filter(msg => msg.messageEntryType === 'ToolResult' && msg.toolResultsPayload?.toolResult);
    
  const toolResults = filteredToolResults
    .sort((a, b) => (a.toolResultsPayload!.toolResult.subIteration ?? 0) - (b.toolResultsPayload!.toolResult.subIteration ?? 0));

  for (const msg of toolResults) {
    const tr = msg.toolResultsPayload!.toolResult;
    const iter = msg.iteration ?? 0;
    const nameKey = `${iter}-${tr.toolName}`;
    const occ = resultOccurrences.get(nameKey) ?? 0;
    resultOccurrences.set(nameKey, occ + 1);
    const key = `${iter}-${tr.toolName}-${occ}`;
    resultsByKey.set(key, { content: tr.content, status: tr.status });
  }

  // Index ToolCall entries by iteration + toolName + occurrence
  const argOccurrences = new Map<string, number>();
  const toolCalls = chatHistory
    .filter(msg => msg.messageEntryType === 'ToolCall' && msg.toolCallPayload?.toolCall);

  for (const msg of toolCalls) {
    const tc = msg.toolCallPayload!.toolCall;
    const iter = msg.iteration ?? 0;
    const nameKey = `${iter}-${tc.toolName}`;
    const occ = argOccurrences.get(nameKey) ?? 0;
    argOccurrences.set(nameKey, occ + 1);
    const key = `${iter}-${tc.toolName}-${occ}`;
    argsByKey.set(key, tc.arguments);
  }

  return { resultsByKey, argsByKey };
}

export interface ToolActionSelection {
  agentName: string;
  iterationName: string;
  iterationIndex: number;
  actionName: string;
  occurrence: number;
  toolId?: string;
}

export function resolveToolActionSelection(
  selection: ToolActionSelection,
  toolActions: Map<string, AgentToolActionInfo[]> | undefined,
  nodeExecutions: NodeExecution[],
  chatHistory: ChatMessageEntry[] | undefined,
): AgentToolActionInfo | null {
  const { agentName, iterationName, iterationIndex, actionName, occurrence, toolId } = selection;
  const tool = toolId ? nodeExecutions.find(node => node.nodeName === agentName)
    ?.iterations?.find(iteration => iteration.index === iterationIndex)
    ?.toolExecutions?.find(item => item.toolId === toolId) : undefined;
  const action = tool ? {
    ...tool, actionName: tool.actionName ?? tool.toolName,
    startTime: tool.startTime, endTime: tool.endTime, duration: tool.duration,
  } : toolActions?.get(`${agentName}-${iterationName}`)
    ?.filter(item => item.status !== 'skipped' && item.actionName === actionName)[occurrence];
  if (!action) return null;
  const history = chatHistory?.filter(message =>
    !message.agentMetadata?.agentName || message.agentMetadata.agentName === agentName);
  return enrichToolAction(action, history ? buildBuiltInToolLookup(history) : null,
    `${iterationIndex}-${actionName}-${occurrence}`);
}

/**
 * Enrich a tool action with chatHistory data when the runtime doesn't provide input/output.
 * Returns the enriched action (or original if it already has data).
 */
export function enrichToolAction(
  action: AgentToolActionInfo,
  lookup: ReturnType<typeof buildBuiltInToolLookup> | null | undefined,
  lookupKey: string,
): AgentToolActionInfo {
  if (action.input != null || action.output != null) {
    return action;
  }
  const chatResult = lookup?.resultsByKey.get(lookupKey);
  const chatArgs = lookup?.argsByKey.get(lookupKey);
  const isFailed = action.status === 'failed';
  
  // Cache the chatResult.content property access
  const content = chatResult?.content;
  const errorMessage = typeof content === 'string'
    ? content
    : content != null
      ? JSON.stringify(content, null, 2)
      : null;

  return {
    ...action,
    input: chatArgs ?? action.input,
    output: isFailed ? undefined : (content ?? action.output),
    ...(isFailed && !action.error ? { error: { code: 'ToolExecutionFailed', message: errorMessage ?? `Tool '${action.actionName}' failed` } } : {}),
  };
}

/**
 * Convert NodeExecution to ExecutionItem for use with the shared
 * `ExecutionListItem`. ChatMessage iterations come from
 * `fetchChatMessageIterationActions` transformed to NodeExecution; we prefix
 * the id with the iteration index so per-turn rows stay unique.
 */
const nodeExecutionToExecutionItem = (
  execution: NodeExecution,
  iterationIndex: number,
): ExecutionItem => {
  const nodeId = `chatmsg-action-${iterationIndex}-${execution.nodeName}`;

  const item: ExecutionItem = {
    nodeId,
    name: execution.nodeName,
    status: execution.status,
    isScope: isScopeNodeType(execution.nodeType),
  };

  if (execution.nodeType) item.type = execution.nodeType;
  if (execution.duration !== undefined) item.duration = execution.duration;
  if (execution.startTime) item.startTime = execution.startTime;
  if (execution.endTime) item.endTime = execution.endTime;
  if (execution.error) item.error = execution.error;
  if (execution.iterationCount !== undefined) item.childCount = execution.iterationCount;

  return item;
};



// =============================================================================
// Types
// =============================================================================

export type SelectedChatMessage = {
  role: 'User' | 'Assistant';
  content: string;
  timestamp: string;
  iteration?: number | undefined;
};

export type ConversationTurn = {
  userMessage: ChatMessageEntry | undefined;
  assistantMessage: ChatMessageEntry | undefined;
  iteration: number;
  taskSequenceId?: string;
};

type ExecutionTimelineIterationMessages = { user?: ChatMessageEntry; assistant?: ChatMessageEntry };

type ExecutionTimelineItem =
  | { type: 'trigger'; sortKey: number; exec: NodeExecution }
  | { type: 'action'; sortKey: number; exec: NodeExecution }
  | { type: 'agent'; sortKey: number; agentName: string; iterations: AgentIterationInfo[]; iterationMessages?: Map<number, ExecutionTimelineIterationMessages> }
  | { type: 'message'; sortKey: number; msg: ChatMessageEntry };

export type ExecutionLogProps = {
  // Data
  sortedExecutions: NodeExecution[];
  chatHistory?: ChatMessageEntry[] | undefined;
  agentIterations?: Map<string, AgentIterationInfo[]> | undefined;
  toolActions?: Map<string, AgentToolActionInfo[]> | undefined;
  scopeRepetitions?: Map<string, RepetitionInfo[]> | undefined;
  scopeActions?: Map<string, NodeExecution[]> | undefined;
  chatMessageIterationActions?: Map<number, NodeExecution[]> | undefined;
  canvasAgentIterations?: Record<string, number> | undefined;
  canvasScopeIterations?: Record<string, number> | undefined;
  childrenByScope: Map<string, NodeExecution[]>;
  conversationTurns: ConversationTurn[];

  // Loading state
  loadingAgentIterations?: Set<string> | undefined;
  loadingToolActions?: Set<string> | undefined;
  loadingScopeRepetitions?: Set<string> | undefined;
  loadingScopeActions?: Set<string> | undefined;
  loadingChatMessageIterations?: Set<number> | undefined;
  toolErrors?: readonly AgentIterationDataError[] | undefined;
  dataErrors?: readonly MonitoringDataError[] | undefined;
  errorRole?: 'alert' | 'note';

  // Mode flags / computed
  useTimelineRendering: boolean;
  isMultiTurnConversation: boolean;
  isConversational: boolean;
  initialUserMessage?: ChatMessageEntry | null | undefined;
  finalAgentResponse?: ChatMessageEntry | null | undefined;

  // Selection state (read)
  selectedNodeId: string | null;
  selectedIterationIndex?: number | undefined;
  selectedToolId?: string | undefined;
  selectedRepetitionName?: string | null | undefined;
  selectedChatMessage: SelectedChatMessage | null;
  highlightedId: string | null;

  // Selection setters
  setSelectedChatMessage?: ((m: SelectedChatMessage | null) => void) | undefined;
  setSelectedToolAction: (a: AgentToolActionInfo | null, identity?: ToolActionSelection) => void;

  // Expand state + togglers (host-owned because effects depend on it)
  expandedAgents: Set<string>;
  expandedScopes: Set<string>;
  expandedRepetitions: Map<string, Set<string>>;
  toggleAgentExpanded: (nodeId: string, agentName: string) => void;
  toggleScopeExpanded: (scopeName: string, nodeType?: string) => void;
  toggleRepetitionExpanded: (scopeName: string, repetitionName: string) => void;

  // Handlers
  onNodeSelect: (nodeId: string, iterationIndex?: number, toolId?: string) => void;
  onIterationChange?: ((nodeId: string, index: number) => void) | undefined;
  onRepetitionSelect?: ((scopeName: string, repetitionName: string) => void) | undefined;
  onFetchToolActions?: ((agentName: string, iterationName: string) => void) | undefined;
  onFetchChatMessageIterationActions?: ((iterationIndex: number) => void) | undefined;
  onFetchScopeActions?: ((scopeName: string, repetitionName: string) => void) | undefined;

  // Refs / DOM
  listRef?: React.RefObject<HTMLDivElement> | undefined;
  itemRefs?: React.MutableRefObject<Map<string, HTMLElement>>;

  /**
   * Map of action name → immediate scope parent name, derived from the
   * workflow definition. Used to indent children of non-iterating scopes
   * (If/Switch/Scope) in the flat execution log so the nesting is visible
   * even though those parents don't expose `parentNodeId` (which only the
   * runtime sets for ForEach/Until inner actions).
   */
  scopeParentByName?: Map<string, string> | undefined;
};

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  executionGroupContainer: {
    contentVisibility: 'auto',
    containIntrinsicSize: 'auto 40px',
  },
  executionList: {
    // Flex into the remaining column height instead of trying to occupy
    // 100% (which, combined with the now fixed-height column header above,
    // would over-extend and get clipped by the parent column's overflow).
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
    ...customScrollbarStyles,
  },
  executionItemRow: {
    display: 'flex',
    alignItems: 'center',
    // Performance: let the browser skip layout + paint for rows scrolled out
    // of view. The size hint stops the row from collapsing while skipped so
    // scroll position stays stable. See web.dev/articles/content-visibility.
    contentVisibility: 'auto',
    containIntrinsicSize: '0 32px',
  },
  nestedRow: {
    paddingLeft: 0,
    '&[data-nest-depth="1"]': { paddingLeft: tokens.spacingHorizontalL },
    '&[data-nest-depth="2"]': { paddingLeft: `calc(${tokens.spacingHorizontalL} * 2)` },
    '&[data-nest-depth="3"]': { paddingLeft: `calc(${tokens.spacingHorizontalL} * 3)` },
    '&[data-nest-depth="4"]': { paddingLeft: `calc(${tokens.spacingHorizontalL} * 4)` },
    '&[data-nest-depth="5"]': { paddingLeft: `calc(${tokens.spacingHorizontalL} * 5)` },
  },
  executionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    borderLeft: '3px solid transparent',
    transitionProperty: 'all',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
  },
  executionItemFailed: {
    borderLeftColor: tokens.colorPaletteRedBorder1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
  executionItemSkipped: {
    opacity: 0.6,
    ':hover': {
      opacity: 0.8,
    },
  },
  executionItemTrigger: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  executionItemAction: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  executionItemAgent: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  executionItemIteration: {
    paddingLeft: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  executionItemTool: {
    paddingLeft: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase100,
  },
  executionItemScope: {
    paddingLeft: tokens.spacingHorizontalXS,
  },
  executionItemRepetition: {
    paddingLeft: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  scopeIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: tokens.spacingHorizontalXS,
  },
  repetitionBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: `1px ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    marginLeft: 'auto',
  },
  executionItemChatMessage: {
    paddingLeft: tokens.spacingHorizontalS,
  },
  chatMessageIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginRight: tokens.spacingHorizontalXS,
  },
  chatMessageRole: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  chatMessageTime: {
    marginLeft: 'auto',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  treeExpandSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '28px',
    height: '24px',
  },
  treeExpandButton: {
    minWidth: tokens.spacingHorizontalL,
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    padding: '0',
    marginRight: tokens.spacingHorizontalXXS,
    '& svg': {
      fontSize: tokens.fontSizeBase200,
    },
  },
  iterationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  iterationIndex: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  nestedGroup: {
    marginLeft: '18px',
    paddingLeft: tokens.spacingHorizontalXS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  nestedGroupDeep: {
    marginLeft: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalXS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  iterationBody: {
    position: 'relative',
    marginLeft: tokens.spacingHorizontalXL,
    paddingLeft: tokens.spacingHorizontalS,
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '4px',
      bottom: '4px',
      width: '1.5px',
      backgroundColor: tokens.colorPalettePurpleForeground2,
      opacity: 0.55,
      borderRadius: '1px',
      '@media (forced-colors: active)': {
        backgroundColor: 'CanvasText',
        opacity: 1,
      },
    },
  },
  iterationChev: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  afterLoopDivider: {
    display: 'block',
    margin: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS} 0`,
    paddingTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  iterationBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
    marginRight: tokens.spacingHorizontalS,
  },
  durationBadge: {
    marginLeft: 'auto',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
  },
  executionItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  executionItemHighlight: {
    animationName: {
      '0%': { backgroundColor: tokens.colorBrandBackground },
      '100%': { backgroundColor: tokens.colorBrandBackground2 },
    },
    animationDuration: tokens.durationUltraSlow,
    animationTimingFunction: tokens.curveDecelerateMax,
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemNameSelected: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
});

// =============================================================================
// Component
// =============================================================================

export const ExecutionLog: React.FC<ExecutionLogProps> = ({
  sortedExecutions,
  chatHistory,
  agentIterations,
  toolActions,
  scopeRepetitions,
  scopeActions,
  chatMessageIterationActions,
  canvasAgentIterations,
  canvasScopeIterations,
  childrenByScope,
  conversationTurns,
  loadingAgentIterations,
  loadingToolActions,
  loadingScopeRepetitions,
  loadingScopeActions,
  loadingChatMessageIterations,
  toolErrors,
  dataErrors,
  errorRole = 'alert',
  useTimelineRendering,
  isMultiTurnConversation,
  isConversational,
  initialUserMessage,
  finalAgentResponse,
  selectedNodeId,
  selectedIterationIndex,
  selectedToolId,
  selectedRepetitionName,
  selectedChatMessage,
  highlightedId,
  setSelectedChatMessage: setSelectedChatMessageProp,
  setSelectedToolAction,
  expandedAgents,
  expandedScopes,
  expandedRepetitions,
  toggleAgentExpanded,
  toggleScopeExpanded,
  toggleRepetitionExpanded,
  onNodeSelect,
  onIterationChange,
  onRepetitionSelect,
  onFetchToolActions,
  onFetchChatMessageIterationActions,
  onFetchScopeActions,
  listRef,
  itemRefs,
  scopeParentByName,
}) => {
  const styles = useStyles();
  const errors = getMonitoringDataErrors(dataErrors, toolErrors);
  // Render-local ownership keeps missing/filtered rows from swallowing an
  // error. Anything not rendered inline gets a named entry in this log.
  const placedErrors = new Set<MonitoringDataError>();
  const actionOwnersByLeaf = React.useMemo(() => {
    const owners = new Map<string, Set<string>>();
    const addOwner = (nodeId: string, parentNodeId?: string) => {
      if (!nodeId.startsWith('action-')) return;
      const leaf = nodeId.slice('action-'.length).split('/').at(-1)!;
      const identities = owners.get(leaf) ?? new Set<string>();
      identities.add(JSON.stringify([nodeId, parentNodeId]));
      owners.set(leaf, identities);
    };
    // Include loaded children even when collapsed, so expanding a scope cannot
    // change which same-named action is assumed to own a flat runtime error.
    for (const nodes of [sortedExecutions, ...childrenByScope.values(), ...(scopeActions?.values() ?? [])]) {
      for (const node of nodes) addOwner(node.nodeId, node.parentNodeId);
    }
    for (const agentName of agentIterations?.keys() ?? []) {
      if (!sortedExecutions.some(node => isAgentExecution(node) && node.nodeName === agentName)) {
        addOwner(`action-${agentName}`);
      }
    }
    return owners;
  }, [sortedExecutions, childrenByScope, scopeActions, agentIterations]);
  // Only bridge a scoped ID to a unique flat alias, never two distinct paths.
  const matchesNode = (error: MonitoringDataError, nodeId: string) => error.nodeId === nodeId
    || (typeof error.nodeId === 'string' && error.nodeId.startsWith('action-') && nodeId.startsWith('action-')
      && error.nodeId.includes('/') !== nodeId.includes('/')
      && error.nodeId.slice('action-'.length).split('/').at(-1) === nodeId.slice('action-'.length).split('/').at(-1)
      && actionOwnersByLeaf.get(nodeId.slice('action-'.length).split('/').at(-1)!)?.size === 1);
  const showErrors = (predicate: (error: MonitoringDataError) => boolean, showContext = false) => {
    const matching = errors.filter(error => !placedErrors.has(error) && predicate(error));
    if (!matching.length) return null;
    matching.forEach(error => placedErrors.add(error));
    return <DataDetailsErrors errors={matching} showContext={showContext} role={errorRole} />;
  };
  const showErrorEntry = (content: React.ReactNode) => content ? <div role="treeitem">{content}</div> : null;
  const showTurnErrors = (messageIndex: number) =>
    showErrorEntry(showErrors(error => error.messageIndex === messageIndex, true));
  const iterationNamesForNode = (nodeId: string) => {
    const execution = sortedExecutions.find(node => node.nodeId === nodeId);
    const name = execution?.nodeName ?? nodeId.replace(/^action-/, '');
    const iterations = agentIterations?.get(name) ?? execution?.iterations ?? [];
    return [
      ...iterations.map(iteration => 'name' in iteration ? iteration.name : String(iteration.index).padStart(6, '0')),
      ...(scopeRepetitions?.get(name) ?? []).map(repetition => repetition.name),
    ];
  };
  const showNodeErrors = (nodeId: string) =>
    showErrors(error => matchesNode(error, nodeId) && error.iterationName === undefined);
  const showIterationErrors = (nodeId: string, iterationName: string) =>
    showErrors(error => matchesNode(error, nodeId) && error.iterationName === iterationName);
  const showCollapsedWarning = (nodeId: string, collapsed: boolean, iterationNames = iterationNamesForNode(nodeId)) => {
    if (!collapsed) return null;
    const matching = errors.filter(error => matchesNode(error, nodeId) && error.iterationName !== undefined
      && iterationNames.includes(error.iterationName));
    if (!matching.length) return null;
    matching.forEach(error => placedErrors.add(error));
    return matching.every(error => error.message === 'Tool details unavailable')
      ? <ToolDetailsWarning /> : <DataDetailsWarning />;
  };

  // The dialog mounts ExecutionLog without a chat-detail pane, so it
  // omits `setSelectedChatMessage`. Fall back to a no-op so chat-row
  // click handlers stay defined and don't need a per-call-site guard,
  // and so chat selection has no visible effect when there is nowhere
  // to render the message body.
  const setSelectedChatMessage = setSelectedChatMessageProp ?? (() => {});

  const isNodeSelected = (nodeId: string) =>
    nodeId === selectedNodeId && selectedIterationIndex === undefined &&
    selectedToolId === undefined && !selectedRepetitionName && !selectedChatMessage;

  const selectNode = (nodeId: string) => {
    setSelectedChatMessage(null);
    setSelectedToolAction(null);
    onNodeSelect(nodeId);
  };

  // Provide a no-op fallback ref map so destructured refs always have a target
  // even when the host doesn't pass `itemRefs` (e.g. inside the dialog where
  // we don't need scroll-into-view tracking).
  const fallbackItemRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const refs = itemRefs ?? fallbackItemRefs;
  // Avoid unused-variable warnings for refs not consumed in every branch.
  void loadingScopeRepetitions;

  // Build a lookup from chatHistory to get per-tool args and output/error
  // Key: "iteration-toolName-occurrenceIndex" to correctly match tools even when
  // the API's action list has different counts than chatHistory
  const builtInToolLookup = React.useMemo(() => {
    if (!chatHistory) return null;
    return buildBuiltInToolLookup(chatHistory);
  }, [chatHistory]);

  // Build the ordered timeline once per data change rather than on every render.
  // The heavy grouping + sort only depends on the run data in the dependency
  // array, so memoizing keeps interaction re-renders (selection, hover, expand)
  // cheap. Returns null when timeline rendering is not in use.
  const timelineModel = React.useMemo(() => {
    if (!useTimelineRendering) return null;

    const timelineItems: ExecutionTimelineItem[] = [];

    // Cache agent execution lookups
    const agentExecutionsMap = new Map<string, NodeExecution>();
    for (const exec of sortedExecutions) {
      if (isAgentExecution(exec)) {
        agentExecutionsMap.set(exec.nodeName, exec);
      }
    }

    const agentToolNames = new Set<string>();
    if (agentIterations) {
      for (const iterations of agentIterations.values()) {
        for (const iteration of iterations) {
          for (const tool of iteration.tools) {
            agentToolNames.add(tool.toolName);
          }
        }
      }
    }
    if (toolActions) {
      for (const actions of toolActions.values()) {
        for (const action of actions) {
          agentToolNames.add(action.toolName);
          agentToolNames.add(action.actionName);
        }
      }
    }

    const messagesByAgentAndIteration = new Map<string, Map<number, ExecutionTimelineIterationMessages>>();
    const messagesGroupedUnderAgents = new Set<ChatMessageEntry>();

    if (chatHistory) {
      for (const msg of chatHistory) {
        if (msg.messageEntryType === 'Content' && msg.timestamp) {
          const agentName = msg.agentMetadata?.agentName;
          const iterationIdx = msg.iteration ?? 0;
          if (agentName) {
            if (!messagesByAgentAndIteration.has(agentName)) {
              messagesByAgentAndIteration.set(agentName, new Map());
            }
            const agentMsgs = messagesByAgentAndIteration.get(agentName)!;
            if (!agentMsgs.has(iterationIdx)) {
              agentMsgs.set(iterationIdx, {});
            }
            const iterMsgs = agentMsgs.get(iterationIdx)!;
            if (msg.role === 'User') {
              iterMsgs.user = msg;
              messagesGroupedUnderAgents.add(msg);
            } else if (msg.role === 'Assistant') {
              iterMsgs.assistant = msg;
              messagesGroupedUnderAgents.add(msg);
            }
          }
        }
      }
    }

    for (const exec of sortedExecutions) {
      if (exec.nodeId.startsWith('trigger-') && exec.startTime) {
        timelineItems.push({
          type: 'trigger',
          sortKey: new Date(exec.startTime).getTime(),
          exec,
        });
      }
    }

    for (const exec of sortedExecutions) {
      if (!exec.nodeId.startsWith('trigger-') && !isAgentExecution(exec) && exec.startTime) {
        if (agentToolNames.has(exec.nodeName)) continue;
        timelineItems.push({
          type: 'action',
          sortKey: new Date(exec.startTime).getTime(),
          exec,
        });
      }
    }

    const timelineAgents = new Map(agentIterations);
    for (const agentName of agentExecutionsMap.keys()) {
      if (!timelineAgents.has(agentName)) timelineAgents.set(agentName, []);
    }
    if (timelineAgents.size > 0) {
      for (const [agentName, iterations] of timelineAgents.entries()) {
        const firstIterStartTime = iterations[0]?.startTime;
        const agentExec = agentExecutionsMap.get(agentName);
        const fallbackTime = agentExec?.startTime ? new Date(agentExec.startTime).getTime() : Date.now();

        const iterationMessages = messagesByAgentAndIteration.get(agentName);

        const firstIterUserMsg = iterationMessages?.get(0)?.user;
        const sortKey = firstIterUserMsg?.timestamp
          ? new Date(firstIterUserMsg.timestamp).getTime()
          : firstIterStartTime
            ? new Date(firstIterStartTime).getTime()
            : fallbackTime;

        const agentItem: ExecutionTimelineItem & { type: 'agent' } = {
          type: 'agent',
          sortKey,
          agentName,
          iterations,
        };
        if (iterationMessages && iterationMessages.size > 0) {
          agentItem.iterationMessages = iterationMessages;
        }
        timelineItems.push(agentItem);
      }
    }

    if (chatHistory) {
      const suppressUnattributedAssistant =
        !isConversational && (agentIterations?.size ?? 0) > 0;
      for (const msg of chatHistory) {
        if (msg.messageEntryType === 'Content' && msg.timestamp && !messagesGroupedUnderAgents.has(msg)) {
          // Only conversation turns belong on the timeline. Other roles
          // (notably the agent's System prompt, which the chatHistory API
          // returns as a Content message) must not render as a stray
          // "Agent response" row. See issues #1679 / #1378.
          if (msg.role !== 'User' && msg.role !== 'Assistant') continue;
          const isUnattributedAssistant = msg.role === 'Assistant' && !msg.agentMetadata?.agentName;
          if (suppressUnattributedAssistant && isUnattributedAssistant) continue;
          timelineItems.push({
            type: 'message',
            sortKey: new Date(msg.timestamp).getTime(),
            msg,
          });
        }
      }
    }

    timelineItems.sort((a, b) => a.sortKey - b.sortKey);
    return { timelineItems, agentExecutionsMap };
  }, [useTimelineRendering, sortedExecutions, agentIterations, toolActions, chatHistory, isConversational]);

  return (
    <div className={styles.executionList} ref={listRef ?? undefined} role="tree" aria-label="Execution log">
      {errors.some(error => !error.nodeId && error.messageIndex === undefined) && (
        <div role="treeitem">
          {showErrors(error => !error.nodeId && error.messageIndex === undefined)}
        </div>
      )}
      {/* === TIMELINE-BASED RENDERING FOR AGENT WORKFLOWS === */}
      {useTimelineRendering ? (
        <>
          {(() => {
            if (!timelineModel || timelineModel.timelineItems.length === 0) return null;
            const { timelineItems, agentExecutionsMap } = timelineModel;
            return timelineItems.map((item) => {
              // TRIGGER
              if (item.type === 'trigger') {
                const exec = item.exec;
                const isSelected = exec.nodeId === selectedNodeId && !selectedChatMessage;
                const isHighlighted = exec.nodeId === highlightedId;
                return (
                  <div key={exec.nodeId} role="treeitem">
                    <button
                      ref={(el) => { if (el) refs.current.set(exec.nodeId, el); }}
                      className={mergeClasses(
                        styles.executionItem,
                        styles.executionItemTrigger,
                        exec.status === 'failed' && styles.executionItemFailed,
                        isSelected && styles.executionItemSelected,
                        isHighlighted && styles.executionItemHighlight
                      )}
                      onClick={() => {
                        setSelectedChatMessage(null);
                        setSelectedToolAction(null);
                        onNodeSelect(exec.nodeId);
                      }}
                      aria-selected={isSelected}
                    >
                      <StatusIcon status={exec.status} />
                      <div className={styles.itemContent}>
                        <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>
                          {exec.nodeName}
                        </Text>
                      </div>
                      {exec.duration && (
                        <Text className={styles.durationBadge}>{formatExecutionDuration(exec.duration)}</Text>
                      )}
                    </button>
                    {showNodeErrors(exec.nodeId)}
                  </div>
                );
              }

              // ACTION (non-agent)
              if (item.type === 'action') {
                const exec = item.exec;
                const isSelected = exec.nodeId === selectedNodeId && !selectedChatMessage;
                const isHighlighted = exec.nodeId === highlightedId;
                return (
                  <div key={exec.nodeId} role="treeitem">
                    <button
                      ref={(el) => { if (el) refs.current.set(exec.nodeId, el); }}
                      className={mergeClasses(
                        styles.executionItem,
                        styles.executionItemAction,
                        exec.status === 'failed' && styles.executionItemFailed,
                        exec.status === 'skipped' && styles.executionItemSkipped,
                        isSelected && styles.executionItemSelected,
                        isHighlighted && styles.executionItemHighlight
                      )}
                      onClick={() => {
                        setSelectedChatMessage(null);
                        setSelectedToolAction(null);
                        onNodeSelect(exec.nodeId);
                      }}
                      aria-selected={isSelected}
                    >
                      <StatusIcon status={exec.status} />
                      <div className={styles.itemContent}>
                        <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>
                          {exec.nodeName}
                        </Text>
                      </div>
                      {exec.duration && (
                        <Text className={styles.durationBadge}>{formatExecutionDuration(exec.duration)}</Text>
                      )}
                    </button>
                    {showNodeErrors(exec.nodeId)}
                  </div>
                );
              }

              // MESSAGE
              if (item.type === 'message') {
                const msg = item.msg;
                const isUser = msg.role === 'User';
                const isSelected = selectedChatMessage?.role === msg.role &&
                  selectedChatMessage?.timestamp === msg.timestamp;
                return (
                  <div key={`${msg.timestamp}-${msg.role}`} role="treeitem">
                    <button
                      className={mergeClasses(
                        styles.executionItem,
                        styles.executionItemChatMessage,
                        isSelected && styles.executionItemSelected
                      )}
                      onClick={() => {
                        setSelectedChatMessage({
                          role: msg.role as 'User' | 'Assistant',
                          content: msg.messageEntryPayload?.content ?? '',
                          timestamp: msg.timestamp,
                          iteration: msg.iteration,
                        });
                      }}
                    >
                      {isUser ? <PersonRegular className={styles.chatMessageIcon} /> : <BotRegular className={styles.chatMessageIcon} />}
                      <div className={styles.itemContent}>
                        <Text className={styles.chatMessageRole}>{isUser ? 'User message' : 'Agent response'}</Text>
                      </div>
                      <Text className={styles.chatMessageTime}>{formatTimeOfDay(msg.timestamp)}</Text>
                    </button>
                  </div>
                );
              }

              // AGENT GROUP with nested iterations
              if (item.type === 'agent') {
                const { agentName, iterations, iterationMessages } = item;
                const agentExec = agentExecutionsMap.get(agentName);
                const agentNodeId = agentExec?.nodeId ?? `action-${agentName}`;
                const isAgentExpanded = expandedAgents.has(agentNodeId);
                const isSelected = isNodeSelected(agentNodeId);

                const agentStatus = iterations.length === 0 ? agentExec?.status ?? 'pending'
                  : iterations.some(i => i.status === 'failed') ? 'failed'
                  : iterations.some(i => i.status === 'running') ? 'running'
                  : iterations.every(i => i.status === 'succeeded') ? 'succeeded'
                  : 'pending';

                const totalDuration = iterations.reduce((sum, i) => sum + (i.duration ?? 0), 0);

                return (
                  <div key={`agent-${agentName}`} role="treeitem" aria-expanded={isAgentExpanded} aria-selected={isSelected} className={styles.executionGroupContainer}>
                    <div className={styles.executionItemRow}>
                      <Button
                        className={styles.treeExpandButton}
                        appearance="subtle"
                        size="small"
                        icon={isAgentExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                        onClick={() => toggleAgentExpanded(agentNodeId, agentName)}
                        aria-label={isAgentExpanded ? 'Collapse' : 'Expand'}
                      />
                      <button
                        type="button"
                        ref={(el) => { if (el) refs.current.set(agentNodeId, el); }}
                        className={mergeClasses(
                          styles.executionItem,
                          styles.executionItemAgent,
                          agentStatus === 'failed' && styles.executionItemFailed,
                          isSelected && styles.executionItemSelected
                        )}
                        onClick={() => selectNode(agentNodeId)}
                        aria-current={isSelected ? 'true' : undefined}
                      >
                        <StatusIcon status={agentStatus} />
                        <div className={styles.itemContent}>
                          <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>{agentName}</Text>
                        </div>
                        <Text className={styles.iterationBadge}>{iterations.length} iter</Text>
                        {showCollapsedWarning(agentNodeId, !isAgentExpanded)}
                        {totalDuration > 0 && (
                          <Text className={styles.durationBadge}>{formatExecutionDuration(totalDuration)}</Text>
                        )}
                      </button>
                    </div>
                    {showNodeErrors(agentNodeId)}

                    {isAgentExpanded && iterations.map((iter) => {
                      const iterationName = iter.name ?? String(iter.index).padStart(6, '0');
                      const iterationKey = `${agentName}-${iterationName}`;
                      const isIterationSelected = agentNodeId === selectedNodeId &&
                        selectedIterationIndex === iter.index && !selectedChatMessage;

                      const actions = toolActions?.get(iterationKey) ?? [];
                      const isLoadingActions = loadingToolActions?.has(iterationKey) ?? false;

                      const iterMsgs = iterationMessages?.get(iter.index);
                      const userMessage = iterMsgs?.user;
                      const responseMessage = iterMsgs?.assistant;

                      if (actions.length === 0 && !isLoadingActions && onFetchToolActions) {
                        setTimeout(() => onFetchToolActions(agentName, iterationName), 0);
                      }

                      return (
                        <div key={iterationKey} className={styles.nestedGroup}>
                          {userMessage && (
                            <button
                              className={mergeClasses(
                                styles.executionItem,
                                styles.executionItemChatMessage,
                                selectedChatMessage?.role === 'User' && selectedChatMessage?.timestamp === userMessage.timestamp && styles.executionItemSelected
                              )}
                              onClick={() => {
                                setSelectedChatMessage({
                                  role: 'User',
                                  content: userMessage.messageEntryPayload?.content ?? '',
                                  timestamp: userMessage.timestamp,
                                  iteration: userMessage.iteration,
                                });
                              }}
                            >
                              <PersonRegular className={styles.chatMessageIcon} />
                              <div className={styles.itemContent}>
                                <Text className={styles.chatMessageRole}>User message</Text>
                              </div>
                              <Text className={styles.chatMessageTime}>{formatTimeOfDay(userMessage.timestamp)}</Text>
                            </button>
                          )}

                          <button
                            ref={(el) => { if (el) refs.current.set(`${agentNodeId}-iter-${iter.index}`, el); }}
                            className={mergeClasses(
                              styles.executionItem,
                              styles.executionItemIteration,
                              iter.status === 'failed' && styles.executionItemFailed,
                              isIterationSelected && styles.executionItemSelected
                            )}
                            onClick={() => {
                              setSelectedChatMessage(null);
                              setSelectedToolAction(null);
                              onNodeSelect(agentNodeId, iter.index);
                              onIterationChange?.(agentName, iter.index);
                            }}
                            aria-selected={isIterationSelected}
                          >
                            <StatusIcon status={iter.status} />
                            <div className={styles.itemContent}>
                              <Text className={mergeClasses(styles.itemName, isIterationSelected && styles.itemNameSelected)}>
                                Iteration {iter.index + 1} of {iterations.length}
                              </Text>
                            </div>
                            {iter.duration && (
                              <Text className={styles.durationBadge}>{formatExecutionDuration(iter.duration)}</Text>
                            )}
                          </button>

                          {showIterationErrors(agentNodeId, iterationName)}
                          {isLoadingActions && actions.length === 0 && (
                            <div className={mergeClasses(styles.loadingIndicator, styles.nestedGroup)}>
                              <Spinner size="extra-tiny" />
                              <Text>Loading...</Text>
                            </div>
                          )}
                          {actions.length > 0 && actions.filter(a => a.status !== 'skipped').map((action, actionIndex) => {
                            const actionId = `action-${actionIndex}`;
                            const isActionSelected = agentNodeId === selectedNodeId &&
                              selectedIterationIndex === iter.index &&
                              selectedToolId === actionId && !selectedChatMessage;
                            const toolNodeId = `tool-${agentName}-${action.actionName}`;

                            return (
                                <button
                                key={`${iterationKey}-action-${actionIndex}`}
                                className={mergeClasses(
                                  styles.executionItem,
                                  styles.executionItemTool,
                                  styles.nestedGroupDeep,
                                  isActionSelected && styles.executionItemSelected
                                )}
                                onClick={() => {
                                  setSelectedChatMessage(null);
                                  const lookupKey = `${iter.index}-${action.actionName}-${actions.filter(a => a.status !== "skipped").slice(0, actionIndex).filter(a => a.actionName === action.actionName).length}`;
                                  setSelectedToolAction(enrichToolAction(action, builtInToolLookup, lookupKey), {
                                    agentName, iterationName, iterationIndex: iter.index, actionName: action.actionName,
                                    occurrence: actions.filter(a => a.status !== 'skipped').slice(0, actionIndex).filter(a => a.actionName === action.actionName).length,
                                  });
                                  onNodeSelect(toolNodeId, iter.index, actionId);
                                  onIterationChange?.(agentName, iter.index);
                                }}
                                  aria-selected={isActionSelected}
                                >
                                  <StatusIcon status={action.status} />
                                  <div className={styles.itemContent}>
                                    <Text className={mergeClasses(styles.itemName, isActionSelected && styles.itemNameSelected)}>
                                      {action.actionName}
                                    </Text>
                                  </div>
                                  {action.duration && (
                                    <Text className={styles.durationBadge}>{formatExecutionDuration(action.duration)}</Text>
                                  )}
                                </button>
                            );
                          })}

                          {responseMessage && (
                            <button
                              className={mergeClasses(
                                styles.executionItem,
                                styles.executionItemChatMessage,
                                selectedChatMessage?.role === 'Assistant' && selectedChatMessage?.timestamp === responseMessage.timestamp && styles.executionItemSelected
                              )}
                              onClick={() => {
                                setSelectedChatMessage({
                                  role: 'Assistant',
                                  content: responseMessage.messageEntryPayload?.content ?? '',
                                  timestamp: responseMessage.timestamp,
                                  iteration: responseMessage.iteration,
                                });
                              }}
                            >
                              <BotRegular className={styles.chatMessageIcon} />
                              <div className={styles.itemContent}>
                                <Text className={styles.chatMessageRole}>Agent response</Text>
                              </div>
                              <Text className={styles.chatMessageTime}>{formatTimeOfDay(responseMessage.timestamp)}</Text>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return null;
            });
          })()}
        </>
      ) : isMultiTurnConversation ? (
        <>
          {(() => {
            const agentExecution = sortedExecutions.find(exec => isAgentExecution(exec));
            const agentName = agentExecution?.nodeName ?? '';
            const fetchedAgentIterations = agentIterations?.get(agentName);
            const allIterations = fetchedAgentIterations ?? agentExecution?.iterations ?? [];
            const isLoadingAgentIter = loadingAgentIterations?.has(agentName) ?? false;

            const turnIterationsMap = new Map<number, typeof allIterations>();

            for (let turnIdx = 0; turnIdx < conversationTurns.length; turnIdx++) {
              const turn = conversationTurns[turnIdx]!;
              const nextTurn = conversationTurns[turnIdx + 1];

              const turnUserMessageTimestamp = turn.userMessage?.timestamp;
              const nextTurnUserMessageTimestamp = nextTurn?.userMessage?.timestamp;

              const turnStart = turnUserMessageTimestamp
                ? new Date(turnUserMessageTimestamp).getTime()
                : 0;
              const turnEnd = nextTurnUserMessageTimestamp
                ? new Date(nextTurnUserMessageTimestamp).getTime()
                : Infinity;

              const turnIters = allIterations.filter(iter => {
                if (!iter.startTime) return false;
                const iterStart = new Date(iter.startTime).getTime();
                return iterStart >= turnStart && iterStart < turnEnd;
              });

              turnIterationsMap.set(turnIdx, turnIters);
            }

            return conversationTurns.map((turn, turnIndex) => {
              const turnIterations = turnIterationsMap.get(turnIndex) ?? [];
              const agentNodeId = agentExecution?.nodeId ?? `action-${agentName}`;
              const isAgentExpanded = expandedAgents.has(agentNodeId);
              const isSelected = isNodeSelected(agentNodeId);
              const hasTurnIterations = turnIterations.length > 0;

              const isChatMessageWorkflow = agentName === 'internal.MessageIterationOrchestrator';
              const iterationIndex = turn.iteration;
              const chatMessageActions = isChatMessageWorkflow
                ? chatMessageIterationActions?.get(iterationIndex) ?? []
                : [];
              const isLoadingChatMessageActions = isChatMessageWorkflow &&
                (loadingChatMessageIterations?.has(iterationIndex) ?? false);

              if (isChatMessageWorkflow &&
                  !chatMessageActions.length &&
                  !isLoadingChatMessageActions &&
                  onFetchChatMessageIterationActions) {
                setTimeout(() => onFetchChatMessageIterationActions(iterationIndex), 0);
              }

              return (
                <React.Fragment key={`turn-${turn.iteration}`}>
                  {turn.userMessage && (
                    <div role="treeitem">
                      <button
                        className={mergeClasses(
                          styles.executionItem,
                          styles.executionItemChatMessage,
                          selectedChatMessage?.role === 'User' &&
                          selectedChatMessage?.iteration === turn.iteration &&
                          styles.executionItemSelected
                        )}
                        onClick={() => {
                          setSelectedChatMessage({
                            role: 'User',
                            content: turn.userMessage?.messageEntryPayload?.content ?? '',
                            timestamp: turn.userMessage?.timestamp ?? '',
                            iteration: turn.iteration,
                          });
                        }}
                      >
                        <PersonRegular className={styles.chatMessageIcon} />
                        <div className={styles.itemContent}>
                          <Text className={styles.chatMessageRole}>User message</Text>
                        </div>
                        <Text className={styles.chatMessageTime}>
                          {formatTimeOfDay(turn.userMessage.timestamp)}
                        </Text>
                      </button>
                    </div>
                  )}

                  {turnIndex === 0 && sortedExecutions
                    .filter(exec => {
                      if (exec.nodeId.startsWith('trigger-')) return true;
                      const isOrchestrator = exec.nodeName === 'internal.MessageIterationOrchestrator';
                      return !isAgentExecution(exec) && !isOrchestrator;
                    })
                    .map((execution) => {
                      const isSelected = execution.nodeId === selectedNodeId && !selectedChatMessage;
                      const isHighlighted = execution.nodeId === highlightedId;
                      const isTrigger = execution.nodeId.startsWith('trigger-');

                      return (
                        <div key={execution.nodeId} role="treeitem">
                          <button
                            ref={(el) => { if (el) refs.current.set(execution.nodeId, el); }}
                            className={mergeClasses(
                              styles.executionItem,
                              isTrigger ? styles.executionItemTrigger : styles.executionItemAction,
                              execution.status === 'failed' && styles.executionItemFailed,
                              execution.status === 'skipped' && styles.executionItemSkipped,
                              isSelected && styles.executionItemSelected,
                              isHighlighted && styles.executionItemHighlight
                            )}
                            onClick={() => {
                              setSelectedChatMessage(null);
                              setSelectedToolAction(null);
                              onNodeSelect(execution.nodeId);
                            }}
                            aria-selected={isSelected}
                          >
                            <StatusIcon status={execution.status} />
                            <div className={styles.itemContent}>
                              <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>
                                {execution.nodeName}
                              </Text>
                            </div>
                            {execution.duration !== undefined && (
                              <Text className={styles.durationBadge}>{formatExecutionDuration(execution.duration)}</Text>
                            )}
                          </button>
                          {showNodeErrors(execution.nodeId)}
                        </div>
                      );
                    })}

                  {isChatMessageWorkflow && (
                    <>
                      {showTurnErrors(iterationIndex)}
                      {isLoadingChatMessageActions && chatMessageActions.length === 0 && (
                        <div className={styles.loadingIndicator}>
                          <Spinner size="extra-tiny" />
                          <Text>Loading actions...</Text>
                        </div>
                      )}
                      {chatMessageActions.map((execution) => {
                        const executionItem = nodeExecutionToExecutionItem(execution, iterationIndex);
                        const isSelected = selectedNodeId === executionItem.nodeId && !selectedChatMessage;
                        const isHighlighted = highlightedId === executionItem.nodeId;

                        return (
                          <React.Fragment key={`${executionItem.nodeId}-turn-${turnIndex}`}>
                          <ExecutionListItem
                            item={executionItem}
                            isSelected={isSelected}
                            isHighlighted={isHighlighted}
                            onClick={() => {
                              setSelectedChatMessage(null);
                              setSelectedToolAction(null);
                              onNodeSelect(executionItem.nodeId, iterationIndex);
                            }}
                            itemRef={(el) => { if (el) refs.current.set(executionItem.nodeId, el); }}
                          />
                          {showErrorEntry(showNodeErrors(executionItem.nodeId))}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}

                  {!isChatMessageWorkflow && agentExecution && (hasTurnIterations
                    || (turnIndex === 0 && errors.some(error => error.nodeId === agentNodeId))) && (
                    <div key={`turn-${turnIndex}-agent`} role="treeitem" aria-expanded={isAgentExpanded} aria-selected={isSelected}>
                      <div className={styles.executionItemRow}>
                        <Button
                          className={styles.treeExpandButton}
                          appearance="subtle"
                          size="small"
                          icon={isAgentExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                          onClick={() => toggleAgentExpanded(agentNodeId, agentName)}
                          aria-label={isAgentExpanded ? 'Collapse' : 'Expand'}
                        />
                        <button
                          type="button"
                          ref={(el) => { if (el) refs.current.set(agentNodeId, el); }}
                          className={mergeClasses(
                            styles.executionItem,
                            styles.executionItemAgent,
                            turnIterations.some(i => i.status === 'failed') && styles.executionItemFailed,
                            isSelected && styles.executionItemSelected
                          )}
                          onClick={() => selectNode(agentNodeId)}
                          aria-current={isSelected ? 'true' : undefined}
                        >
                          <StatusIcon status={
                            turnIterations.length === 0 ? agentExecution.status
                            : turnIterations.some(i => i.status === 'failed') ? 'failed'
                            : turnIterations.some(i => i.status === 'running') ? 'running'
                            : turnIterations.every(i => i.status === 'succeeded') ? 'succeeded'
                            : 'pending'
                          } />
                          <div className={styles.itemContent}>
                            <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>{agentName}</Text>
                          </div>
                          <Text className={styles.iterationBadge}>{turnIterations.length} iter</Text>
                          {showCollapsedWarning(agentNodeId, !isAgentExpanded,
                            turnIterations.map(iter => 'name' in iter && iter.name ? iter.name : String(iter.index).padStart(6, '0')))}
                          {(() => {
                            const totalDuration = turnIterations.reduce((sum, i) => sum + (i.duration ?? 0), 0);
                            return totalDuration > 0 && (
                              <Text className={styles.durationBadge}>{formatExecutionDuration(totalDuration)}</Text>
                            );
                          })()}
                        </button>
                      </div>
                      {showNodeErrors(agentNodeId)}

                      {isAgentExpanded && turnIterations.map((iter) => {
                        const iterationName = 'name' in iter && iter.name ? iter.name : String(iter.index).padStart(6, '0');
                        const iterationKey = `${agentName}-${iterationName}`;
                        const isIterationSelected = agentNodeId === selectedNodeId &&
                          selectedIterationIndex === iter.index && !selectedChatMessage;

                        const actions = toolActions?.get(iterationKey) ?? [];
                        const isLoadingActions = loadingToolActions?.has(iterationKey) ?? false;

                        if (actions.length === 0 && !isLoadingActions && onFetchToolActions) {
                          setTimeout(() => onFetchToolActions(agentName, iterationName), 0);
                        }

                        return (
                          <div key={iterationKey} className={styles.nestedGroup}>
                            <button
                              ref={(el) => { if (el) refs.current.set(`${agentNodeId}-iter-${iter.index}`, el); }}
                              className={mergeClasses(
                                styles.executionItem,
                                styles.executionItemIteration,
                                iter.status === 'failed' && styles.executionItemFailed,
                                isIterationSelected && styles.executionItemSelected
                              )}
                              onClick={() => {
                                setSelectedChatMessage(null);
                                setSelectedToolAction(null);
                                onNodeSelect(agentNodeId, iter.index);
                                onIterationChange?.(agentName, iter.index);
                              }}
                              aria-selected={isIterationSelected}
                            >
                              <StatusIcon status={iter.status} />
                              <div className={styles.itemContent}>
                                <Text className={mergeClasses(styles.itemName, isIterationSelected && styles.itemNameSelected)}>
                                  Iteration {iter.index + 1}
                                </Text>
                              </div>
                              {iter.duration && (
                                <Text className={styles.durationBadge}>{formatExecutionDuration(iter.duration)}</Text>
                              )}
                            </button>

                            {showIterationErrors(agentNodeId, iterationName)}
                            {isLoadingActions && actions.length === 0 && (
                              <div className={mergeClasses(styles.loadingIndicator, styles.nestedGroup)}>
                                <Spinner size="extra-tiny" />
                                <Text>Loading...</Text>
                              </div>
                            )}
                            {actions.length > 0 && actions.filter(a => a.status !== 'skipped').map((action, actionIndex) => {
                              const actionId = `action-${actionIndex}`;
                              const isActionSelected = agentNodeId === selectedNodeId &&
                                selectedIterationIndex === iter.index &&
                                selectedToolId === actionId && !selectedChatMessage;
                              const toolNodeId = `tool-${agentName}-${action.actionName}`;

                              return (
                                  <button
                                  key={`${iterationKey}-action-${actionIndex}`}
                                  className={mergeClasses(
                                    styles.executionItem,
                                    styles.executionItemTool,
                                    styles.nestedGroupDeep,
                                    isActionSelected && styles.executionItemSelected
                                  )}
                                  onClick={() => {
                                    setSelectedChatMessage(null);
                                    const lookupKey = `${iter.index}-${action.actionName}-${actions.filter(a => a.status !== "skipped").slice(0, actionIndex).filter(a => a.actionName === action.actionName).length}`;
                                    setSelectedToolAction(enrichToolAction(action, builtInToolLookup, lookupKey), {
                                      agentName, iterationName, iterationIndex: iter.index, actionName: action.actionName,
                                      occurrence: actions.filter(a => a.status !== 'skipped').slice(0, actionIndex).filter(a => a.actionName === action.actionName).length,
                                    });
                                    onNodeSelect(toolNodeId, iter.index, actionId);
                                    onIterationChange?.(agentName, iter.index);
                                  }}
                                  aria-selected={isActionSelected}
                                >
                                  <StatusIcon status={action.status} />
                                  <div className={styles.itemContent}>
                                    <Text className={mergeClasses(styles.itemName, isActionSelected && styles.itemNameSelected)}>
                                      {action.actionName}
                                    </Text>
                                  </div>
                                  {action.duration && (
                                    <Text className={styles.durationBadge}>{formatExecutionDuration(action.duration)}</Text>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!isChatMessageWorkflow && agentExecution && !hasTurnIterations && isLoadingAgentIter && (
                    <div className={styles.loadingIndicator}>
                      <Spinner size="extra-tiny" />
                      <Text>Loading iterations...</Text>
                    </div>
                  )}

                  {turn.assistantMessage && (
                    <div role="treeitem">
                      <button
                        className={mergeClasses(
                          styles.executionItem,
                          styles.executionItemChatMessage,
                          selectedChatMessage?.role === 'Assistant' &&
                          selectedChatMessage?.iteration === turn.iteration &&
                          styles.executionItemSelected
                        )}
                        onClick={() => {
                          setSelectedChatMessage({
                            role: 'Assistant',
                            content: turn.assistantMessage?.messageEntryPayload?.content ?? '',
                            timestamp: turn.assistantMessage?.timestamp ?? '',
                            iteration: turn.iteration,
                          });
                        }}
                      >
                        <BotRegular className={styles.chatMessageIcon} />
                        <div className={styles.itemContent}>
                          <Text className={styles.chatMessageRole}>Agent response</Text>
                        </div>
                        <Text className={styles.chatMessageTime}>
                          {formatTimeOfDay(turn.assistantMessage.timestamp)}
                        </Text>
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            });
          })()}
        </>
      ) : (
        <>
          {initialUserMessage && (
            <div role="treeitem">
              <button
                className={mergeClasses(
                  styles.executionItem,
                  styles.executionItemChatMessage,
                  selectedChatMessage?.role === 'User' && styles.executionItemSelected
                )}
                onClick={() => {
                  setSelectedChatMessage({
                    role: 'User',
                    content: initialUserMessage.messageEntryPayload?.content ?? '',
                    timestamp: initialUserMessage.timestamp,
                    iteration: initialUserMessage.iteration ?? 0,
                  });
                }}
              >
                <PersonRegular className={styles.chatMessageIcon} />
                <div className={styles.itemContent}>
                  <Text className={styles.chatMessageRole}>User message</Text>
                </div>
                <Text className={styles.chatMessageTime}>
                  {formatTimeOfDay(initialUserMessage.timestamp)}
                </Text>
              </button>
            </div>
          )}

          {(() => {
            const orchestratorExecution = sortedExecutions.find(exec => exec.nodeName === 'internal.MessageIterationOrchestrator');
            const isChatMessageWorkflow = !!orchestratorExecution;

            if (!isChatMessageWorkflow) return null;

            const iterationIndex = 0;
            const chatMessageActions = chatMessageIterationActions?.get(iterationIndex) ?? [];
            const isLoadingActions = loadingChatMessageIterations?.has(iterationIndex) ?? false;

            if (!chatMessageActions.length && !isLoadingActions && onFetchChatMessageIterationActions) {
              setTimeout(() => onFetchChatMessageIterationActions(iterationIndex), 0);
            }

            const triggerExecution = sortedExecutions.find(exec => exec.nodeId.startsWith('trigger-'));

            return (
              <>
                {triggerExecution && (
                  <div role="treeitem">
                    <button
                      ref={(el) => { if (el) refs.current.set(triggerExecution.nodeId, el); }}
                      className={mergeClasses(
                        styles.executionItem,
                        styles.executionItemTrigger,
                        triggerExecution.status === 'failed' && styles.executionItemFailed,
                        triggerExecution.nodeId === selectedNodeId && !selectedChatMessage && styles.executionItemSelected,
                        triggerExecution.nodeId === highlightedId && styles.executionItemHighlight
                      )}
                      onClick={() => {
                        setSelectedChatMessage(null);
                        setSelectedToolAction(null);
                        onNodeSelect(triggerExecution.nodeId);
                      }}
                      aria-selected={triggerExecution.nodeId === selectedNodeId && !selectedChatMessage}
                    >
                      <StatusIcon status={triggerExecution.status} />
                      <div className={styles.itemContent}>
                        <Text className={mergeClasses(styles.itemName, triggerExecution.nodeId === selectedNodeId && styles.itemNameSelected)}>
                          {triggerExecution.nodeName}
                        </Text>
                      </div>
                      {triggerExecution.duration && (
                        <Text className={styles.durationBadge}>{formatExecutionDuration(triggerExecution.duration)}</Text>
                      )}
                    </button>
                  </div>
                )}

                {isLoadingActions && chatMessageActions.length === 0 && (
                  <div className={styles.loadingIndicator}>
                    <Spinner size="extra-tiny" />
                    <Text>Loading actions...</Text>
                  </div>
                )}
                {showTurnErrors(iterationIndex)}

                {chatMessageActions.map((execution) => {
                  const executionItem = nodeExecutionToExecutionItem(execution, iterationIndex);
                  const isSelected = executionItem.nodeId === selectedNodeId ||
                    (selectedNodeId === `action-${execution.nodeName}` && selectedIterationIndex === iterationIndex);
                  const isHighlighted = executionItem.nodeId === highlightedId || `action-${execution.nodeName}` === highlightedId;

                  return (
                    <React.Fragment key={executionItem.nodeId}>
                    <ExecutionListItem
                      item={executionItem}
                      isSelected={isSelected && !selectedChatMessage}
                      isHighlighted={isHighlighted}
                      onClick={() => {
                        setSelectedChatMessage(null);
                        setSelectedToolAction(null);
                        onNodeSelect(executionItem.nodeId, iterationIndex);
                      }}
                      itemRef={(el) => { if (el) refs.current.set(executionItem.nodeId, el); }}
                    />
                    {showErrorEntry(showNodeErrors(executionItem.nodeId))}
                    </React.Fragment>
                  );
                })}
              </>
            );
          })()}

          {(() => {
            const agentToolActionNames = new Set<string>();
            if (agentIterations) {
              for (const iterations of agentIterations.values()) {
                for (const iteration of iterations) {
                  for (const tool of iteration.tools) {
                    agentToolActionNames.add(tool.toolName);
                  }
                }
              }
            }
            if (toolActions) {
              for (const actions of toolActions.values()) {
                for (const action of actions) {
                  agentToolActionNames.add(action.toolName);
                  agentToolActionNames.add(action.actionName);
                }
              }
            }

            const hasOrchestrator = sortedExecutions.some(
              (exec) => exec.nodeName === 'internal.MessageIterationOrchestrator',
            );
            const executionByName = new Map<string, NodeExecution>();
            for (const exec of sortedExecutions) {
              executionByName.set(exec.nodeName, exec);
            }
            const filteredExecutions = hasOrchestrator
              ? []
              : sortedExecutions
                  .filter((exec) => exec.nodeName !== 'internal.MessageIterationOrchestrator')
                  .filter((exec) => !agentToolActionNames.has(exec.nodeName))
                  .filter((exec) => {
                    if (!exec.parentNodeId) return true;
                    const parent = executionByName.get(exec.parentNodeId);
                    if (!parent) return true;
                    const parentType = parent.nodeType.toLowerCase();
                    return !parentType.includes('until') && !parentType.includes('foreach');
                  });

            const getNonIteratingScopeDepth = (exec: NodeExecution): number => {
              if (!scopeParentByName) return 0;
              let depth = 0;
              let currentName: string | undefined = exec.nodeName;
              const seen = new Set<string>();
              while (currentName) {
                const parentName = scopeParentByName.get(currentName);
                if (!parentName || seen.has(parentName)) break;
                seen.add(parentName);
                const parent = executionByName.get(parentName);
                if (parent) {
                  const ptype = parent.nodeType.toLowerCase();
                  if (ptype.includes('foreach') || ptype.includes('until')) break;
                  if (isScopeNodeType(parent.nodeType)) {
                    depth += 1;
                  }
                }
                currentName = parentName;
              }
              return depth;
            };

            return filteredExecutions.map((execution, idx) => {
              const prev = idx > 0 ? filteredExecutions[idx - 1] : null;
              const showAfterLoopDivider =
                !!prev &&
                !execution.parentNodeId &&
                isScopeNodeType(prev.nodeType) &&
                prev.nodeType.toLowerCase().includes('foreach') &&
                !execution.nodeType.toLowerCase().includes('foreach');
              const isSelected = isNodeSelected(execution.nodeId);
              const isHighlighted = execution.nodeId === highlightedId;
              const isTrigger = execution.nodeId.startsWith('trigger-');
              const agentName = execution.nodeName;
              const fetchedAgentIterations = agentIterations?.get(agentName);
              const iterations = fetchedAgentIterations ?? execution.iterations ?? [];
              const hasIterations = checkHasAgentIterations(execution, fetchedAgentIterations);
              const isAgentType = isAgentExecution(execution);
              const isAgent = isAgentType && hasIterations;
              const isScope = isScopeNodeType(execution.nodeType);
              const isAgentExpanded = expandedAgents.has(execution.nodeId);
              const isScopeExpanded = expandedScopes.has(execution.nodeName);
              const isLoadingAgentIter = loadingAgentIterations?.has(agentName) ?? false;
              const scopeName = execution.nodeName;
              const repetitions = scopeRepetitions?.get(scopeName) ?? [];
              const isLoadingRepetitions = loadingScopeRepetitions?.has(scopeName) ?? false;

              const getItemTypeClass = () => {
                if (isTrigger) return styles.executionItemTrigger;
                if (isAgentType) return styles.executionItemAgent;
                if (isScope) return styles.executionItemScope;
                return styles.executionItemAction;
              };

              const isExpandable = isAgent || (isScope && isIteratingScopeNodeType(execution.nodeType));
              const isExpanded = isAgent ? isAgentExpanded : isScopeExpanded;
              const nestedDepth = getNonIteratingScopeDepth(execution);

              return (
                <React.Fragment key={execution.nodeId}>
                  {showAfterLoopDivider && (
                    <div className={styles.afterLoopDivider} aria-hidden>
                      After loop
                    </div>
                  )}
                  <div
                    role="treeitem"
                    aria-expanded={isExpandable ? isExpanded : undefined}
                    aria-level={nestedDepth + 1}
                    data-nest-depth={Math.min(nestedDepth, 5)}
                    className={styles.nestedRow}
                  >
                    <div className={styles.executionItemRow}>
                      {isExpandable && (
                        <div className={styles.treeExpandSlot}>
                          <Button
                            className={styles.treeExpandButton}
                            appearance="subtle"
                            size="small"
                            icon={isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                            onClick={() => {
                              if (isAgent) {
                                toggleAgentExpanded(execution.nodeId, agentName);
                              } else if (isScope) {
                                toggleScopeExpanded(scopeName, execution.nodeType);
                              }
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          />
                        </div>
                      )}
                      <button
                        ref={(el) => { if (el) refs.current.set(execution.nodeId, el); }}
                        className={mergeClasses(
                          styles.executionItem,
                          getItemTypeClass(),
                          execution.status === 'failed' && styles.executionItemFailed,
                          execution.status === 'skipped' && styles.executionItemSkipped,
                          isSelected && styles.executionItemSelected,
                          isHighlighted && styles.executionItemHighlight
                        )}
                        onClick={() => selectNode(execution.nodeId)}
                        aria-selected={isSelected}
                      >
                        {isScope && !isAgent && isIteratingScopeNodeType(execution.nodeType) && (
                          <ArrowRepeatAllRegular className={styles.scopeIcon} />
                        )}
                        <StatusIcon status={execution.status} />
                        <div className={styles.itemContent}>
                          <Text className={mergeClasses(styles.itemName, isSelected && styles.itemNameSelected)}>
                            {execution.nodeName}
                          </Text>
                        </div>
                        {isAgent && (iterations.length > 0 || (execution.iterationCount ?? 0) > 0) && (
                          <Text className={styles.durationBadge}>
                            {execution.iterationCount ?? iterations.length} iter
                          </Text>
                        )}
                        {showCollapsedWarning(execution.nodeId, isAgent ? !isAgentExpanded : !isScopeExpanded)}
                        {isScope && !isAgent && repetitions.length > 0 && (
                          <Text className={styles.repetitionBadge}>
                            {repetitions.length}
                          </Text>
                        )}
                        {!isExpandable && execution.duration && (
                          <Text className={styles.durationBadge}>
                            {formatExecutionDuration(execution.duration)}
                          </Text>
                        )}
                      </button>
                    </div>
                    {showNodeErrors(execution.nodeId)}

                    {isAgent && isAgentExpanded && isLoadingAgentIter && (
                      <div className={styles.loadingIndicator}>
                        <Spinner size="extra-tiny" />
                        <Text>Loading iterations...</Text>
                      </div>
                    )}

                    {isAgent && isAgentExpanded && !isLoadingAgentIter && iterations.map((iteration) => {
                      const canvasIterationIndex = canvasAgentIterations?.[agentName] ?? 0;
                      const isIterationOnCanvas = iteration.index === canvasIterationIndex;
                      const localToolExecutions = 'toolExecutions' in iteration ? (iteration.toolExecutions ?? []) : [];

                      return (
                        <div key={`${execution.nodeId}-iter-${iteration.index}`} role="group">
                          <button
                            className={mergeClasses(
                              styles.executionItem,
                              styles.executionItemIteration
                            )}
                            onClick={() => {
                              setSelectedChatMessage(null);
                              setSelectedToolAction(null);
                              onNodeSelect(execution.nodeId, iteration.index);
                              onIterationChange?.(agentName, iteration.index);
                            }}
                            aria-selected={false}
                          >
                            <StatusIcon status={iteration.status} />
                            <div className={styles.itemContent}>
                              <div className={styles.iterationHeader}>
                                <span className={styles.iterationIndex}>Iteration {iteration.index + 1}</span>
                                <span>of {iterations.length}</span>
                              </div>
                            </div>
                            {iteration.duration && (
                              <Text className={styles.durationBadge}>
                                {formatExecutionDuration(iteration.duration)}
                              </Text>
                            )}
                          </button>

                          {(() => {
                            const iterationName = 'name' in iteration ? iteration.name : String(iteration.index).padStart(6, '0');
                            const iterationKey = `${agentName}-${iterationName}`;
                            const actions = toolActions?.get(iterationKey) ?? [];
                            const isLoadingActions = loadingToolActions?.has(iterationKey) ?? false;

                            return (
                              <>
                                {showIterationErrors(execution.nodeId, iterationName)}
                                {isLoadingActions && actions.length === 0 && (
                                  <div className={styles.loadingIndicator}>
                                    <Spinner size="extra-tiny" />
                                    <Text>Loading...</Text>
                                  </div>
                                )}
                                {actions.length > 0 && actions.filter(a => a.status !== 'skipped').map((action, actionIndex) => {
                                  const actionId = `action-${actionIndex}`;
                                  const isSelectedFromLogs = execution.nodeId === selectedNodeId &&
                                    selectedIterationIndex === iteration.index &&
                                    selectedToolId === actionId;
                                  const isSelectedFromCanvas = isIterationOnCanvas &&
                                    selectedNodeId?.startsWith('tool-') &&
                                    selectedNodeId.endsWith(`-${action.actionName}`);
                                  const isActionSelected = isSelectedFromLogs || isSelectedFromCanvas;
                                  const toolNodeId = `tool-${agentName}-${action.actionName}`;

                                  return (
                                    <button
                                      key={`${execution.nodeId}-iter-${iteration.index}-action-${actionIndex}`}
                                      className={mergeClasses(
                                        styles.executionItem,
                                        styles.executionItemTool,
                                        isActionSelected && !selectedChatMessage && styles.executionItemSelected
                                      )}
                                      onClick={() => {
                                        setSelectedChatMessage(null);
                                        setSelectedToolAction(action, {
                                          agentName, iterationName, iterationIndex: iteration.index, actionName: action.actionName,
                                          occurrence: actions.filter(a => a.status !== 'skipped').slice(0, actionIndex).filter(a => a.actionName === action.actionName).length,
                                        });
                                        onNodeSelect(toolNodeId, iteration.index, actionId);
                                        onIterationChange?.(agentName, iteration.index);
                                      }}
                                      aria-selected={isActionSelected && !selectedChatMessage}
                                    >
                                      <StatusIcon status={action.status} />
                                      <div className={styles.itemContent}>
                                        <Text className={mergeClasses(styles.itemName, isActionSelected && styles.itemNameSelected)}>
                                          {action.actionName}
                                        </Text>
                                      </div>
                                      {action.duration && (
                                        <Text className={styles.durationBadge}>
                                          {formatExecutionDuration(action.duration)}
                                        </Text>
                                      )}
                                    </button>
                                  );
                                })}
                              </>
                            );
                          })()}

                          {localToolExecutions.map((tool, toolIdx) => {
                            const isToolSelected = execution.nodeId === selectedNodeId &&
                              selectedIterationIndex === iteration.index &&
                              selectedToolId === tool.toolId;

                            return (
                              <button
                                key={`${execution.nodeId}-iter-${iteration.index}-tool-${tool.toolId}`}
                                className={mergeClasses(
                                  styles.executionItem,
                                  styles.executionItemTool,
                                  isToolSelected && !selectedChatMessage && styles.executionItemSelected
                                )}
                                onClick={() => {
                                  setSelectedChatMessage(null);
                                  
                                  // Track occurrences up to this index
                                  const toolActionName = tool.actionName ?? tool.toolName;
                                  let currentCount = 0;
                                  for (let i = 0; i < toolIdx; i++) {
                                    const t = localToolExecutions[i]!;
                                    if ((t.actionName ?? t.toolName) === toolActionName) {
                                      currentCount++;
                                    }
                                  }
                                  
                                  const lookupKey = `${iteration.index}-${toolActionName}-${currentCount}`;
                                  const baseAction: AgentToolActionInfo = {
                                    toolName: tool.toolName,
                                    actionName: toolActionName,
                                    status: tool.status,
                                    startTime: tool.startTime,
                                    endTime: tool.endTime,
                                    duration: tool.duration,
                                    input: tool.input,
                                    output: tool.output,
                                  };
                                  setSelectedToolAction(enrichToolAction(baseAction, builtInToolLookup, lookupKey), {
                                    agentName, iterationName: String(iteration.index).padStart(6, '0'),
                                    iterationIndex: iteration.index, actionName: toolActionName,
                                    occurrence: currentCount, toolId: tool.toolId,
                                  });
                                  onNodeSelect(execution.nodeId, iteration.index, tool.toolId);
                                }}
                                aria-selected={isToolSelected && !selectedChatMessage}
                              >
                                <StatusIcon status={tool.status} />
                                <div className={styles.itemContent}>
                                  <Text className={mergeClasses(styles.itemName, isToolSelected && styles.itemNameSelected)}>
                                    {tool.actionName ?? tool.toolName}
                                  </Text>
                                </div>
                                {tool.duration && (
                                  <Text className={styles.durationBadge}>
                                    {formatExecutionDuration(tool.duration)}
                                  </Text>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}

                    {isAgent && isAgentExpanded && !isLoadingAgentIter && iterations.length === 0
                      && !errors.some(error => matchesNode(error, execution.nodeId) && error.message === 'Iterations unavailable') && (
                      <div className={styles.loadingIndicator}>
                        <Text>No iterations found</Text>
                      </div>
                    )}

                    {isScope && !isAgent && isScopeExpanded && execution.nodeType.toLowerCase().includes('foreach') && (
                      <>
                        {isLoadingRepetitions && (
                          <div className={styles.loadingIndicator}>
                            <Spinner size="extra-tiny" />
                            <Text>Loading iterations...</Text>
                          </div>
                        )}

                        {!isLoadingRepetitions && repetitions.map((repetition) => {
                          void canvasScopeIterations;
                          const isRepetitionSelected = execution.nodeId === selectedNodeId && selectedRepetitionName === repetition.name;
                          const isRepetitionExpanded =
                            expandedRepetitions.get(scopeName)?.has(repetition.name) ?? false;

                          const actionKey = `${scopeName}-${repetition.name}`;
                          const fetchedActions = scopeActions?.get(actionKey);
                          const repetitionActionList = (fetchedActions && fetchedActions.length > 0)
                            ? fetchedActions
                            : (childrenByScope.get(scopeName) ?? []);
                          const isLoadingActions = loadingScopeActions?.has(actionKey) ?? false;

                          return (
                            <div key={`${execution.nodeId}-rep-${repetition.name}`} role="group">
                              <button
                                className={mergeClasses(
                                  styles.executionItem,
                                  styles.executionItemRepetition,
                                  isRepetitionSelected && styles.executionItemSelected
                                )}
                                onClick={() => {
                                  setSelectedChatMessage(null);
                                  setSelectedToolAction(null);
                                  onNodeSelect(execution.nodeId);
                                  onRepetitionSelect?.(scopeName, repetition.name);
                                  toggleRepetitionExpanded(scopeName, repetition.name);
                                  onFetchScopeActions?.(scopeName, repetition.name);
                                }}
                                aria-selected={isRepetitionSelected}
                                aria-expanded={isRepetitionExpanded}
                              >
                                {isRepetitionExpanded ? (
                                  <ChevronDownRegular className={styles.iterationChev} aria-hidden />
                                ) : (
                                  <ChevronRightRegular className={styles.iterationChev} aria-hidden />
                                )}
                                <StatusIcon status={repetition.status} />
                                <div className={styles.itemContent}>
                                  <div className={styles.iterationHeader}>
                                    <span className={styles.iterationIndex}>Iteration {repetition.index + 1}</span>
                                  </div>
                                </div>
                                {repetition.duration !== undefined && (
                                  <Text className={styles.durationBadge}>
                                    {formatExecutionDuration(repetition.duration)}
                                  </Text>
                                )}
                              </button>

                              {showIterationErrors(execution.nodeId, repetition.name)}
                              {isRepetitionExpanded && (
                                <div className={styles.iterationBody}>
                                  {isLoadingActions && repetitionActionList.length === 0 && (
                                    <div className={styles.loadingIndicator}>
                                      <Spinner size="extra-tiny" />
                                      <Text>Loading...</Text>
                                    </div>
                                  )}

                                  {repetitionActionList.map((action) => {
                                    const isActionSelected = selectedNodeId === action.nodeId && selectedRepetitionName === repetition.name;

                                    return (
                                      <React.Fragment key={`${execution.nodeId}-rep-${repetition.name}-${action.nodeId}`}>
                                      <button
                                        className={mergeClasses(
                                          styles.executionItem,
                                          styles.executionItemTool,
                                          isActionSelected && !selectedChatMessage && styles.executionItemSelected
                                        )}
                                        onClick={() => {
                                          setSelectedChatMessage(null);
                                          setSelectedToolAction(null);
                                          onRepetitionSelect?.(scopeName, repetition.name);
                                          onNodeSelect(action.nodeId);
                                        }}
                                        aria-selected={isActionSelected && !selectedChatMessage}
                                      >
                                        <StatusIcon status={action.status} />
                                        <div className={styles.itemContent}>
                                          <Text className={mergeClasses(styles.itemName, isActionSelected && styles.itemNameSelected)}>
                                            {action.nodeName}
                                          </Text>
                                        </div>
                                        {action.duration !== undefined && (
                                          <Text className={styles.durationBadge}>
                                            {formatExecutionDuration(action.duration)}
                                          </Text>
                                        )}
                                      </button>
                                      {showNodeErrors(action.nodeId)}
                                      {showIterationErrors(action.nodeId, repetition.name)}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {!isLoadingRepetitions && repetitions.length === 0
                          && !errors.some(error => matchesNode(error, execution.nodeId) && error.message === 'Iterations unavailable') && (
                          <div className={styles.loadingIndicator}>
                            <Text>No iterations found</Text>
                          </div>
                        )}
                      </>
                    )}

                    {isScope && !isAgent && isScopeExpanded && execution.nodeType.toLowerCase().includes('until') && (
                      <>
                        {(childrenByScope.get(scopeName) ?? []).map((child) => {
                          const isChildSelected = child.nodeId === selectedNodeId;

                          return (
                            <React.Fragment key={`${execution.nodeId}-child-${child.nodeId}`}>
                            <button
                                className={mergeClasses(
                                  styles.executionItem,
                                  styles.executionItemTool,
                                  isChildSelected && !selectedChatMessage && styles.executionItemSelected
                                )}
                                onClick={() => {
                                  setSelectedChatMessage(null);
                                  setSelectedToolAction(null);
                                  onNodeSelect(child.nodeId);
                                }}
                                aria-selected={isChildSelected && !selectedChatMessage}
                              >
                                <StatusIcon status={child.status} />
                                <div className={styles.itemContent}>
                                  <Text className={mergeClasses(styles.itemName, isChildSelected && styles.itemNameSelected)}>
                                    {child.nodeName}
                                  </Text>
                                </div>
                                {child.iterationCount !== undefined && child.iterationCount > 0 && (
                                  <Text className={styles.durationBadge}>
                                    {child.iterationCount}x
                                  </Text>
                                )}
                                {child.duration !== undefined && (
                                  <Text className={styles.durationBadge}>
                                    {formatExecutionDuration(child.duration)}
                                  </Text>
                                )}
                              </button>
                              {showNodeErrors(child.nodeId)}
                            </React.Fragment>
                            );
                          })}
                        {(childrenByScope.get(scopeName)?.length ?? 0) === 0 && (
                          <div className={styles.loadingIndicator}>
                            <Text>No child actions found</Text>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            });
          })()}

          {finalAgentResponse && (
            <div role="treeitem">
              <button
                className={mergeClasses(
                  styles.executionItem,
                  styles.executionItemChatMessage,
                  selectedChatMessage?.role === 'Assistant' && styles.executionItemSelected
                )}
                onClick={() => {
                  setSelectedChatMessage({
                    role: 'Assistant',
                    content: finalAgentResponse.messageEntryPayload?.content ?? '',
                    timestamp: finalAgentResponse.timestamp,
                    iteration: finalAgentResponse.iteration ?? 0,
                  });
                }}
              >
                <BotRegular className={styles.chatMessageIcon} />
                <div className={styles.itemContent}>
                  <Text className={styles.chatMessageRole}>Agent response</Text>
                </div>
                <Text className={styles.chatMessageTime}>
                  {formatTimeOfDay(finalAgentResponse.timestamp)}
                </Text>
              </button>
            </div>
          )}
        </>
      )}
      {showErrorEntry(showErrors(() => true, true))}
    </div>
  );
};
